import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GAMES, STRATEGIES } from "../../data/games";
import { generateOptimizedGrid } from "../../utils/lottery";
import { LotteryBall } from "../LotteryBall";
import { ConformityGauge, scoreLevel } from "../ConformityGauge";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Sparkles, Copy, Lock, Zap } from "lucide-react";
import { PlayFdjButton } from "../FdjAffiliate";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export const GeneratorTab = ({ game, draws, affiliateUrl, onNeedAuth, onNeedPremium }) => {
  const cfg = GAMES[game];
  const { user, refreshUsage, usage } = useAuth();
  const [strategy, setStrategy] = useState("balanced");
  const [kenoPick, setKenoPick] = useState(10);
  const [grids, setGrids] = useState([]);
  const [generating, setGenerating] = useState(false);

  const bonusVariant = cfg.bonusType;

  const generate = async (count) => {
    if (!user) {
      onNeedAuth();
      return;
    }
    // Freemium gate (server-enforced). Free users: 1 grid/game/day → only count=1 allowed.
    try {
      const { data } = await api.post("/usage/consume", { game, count });
      if (!data.allowed) {
        onNeedPremium();
        return;
      }
      await refreshUsage();
    } catch (e) {
      toast.error("Erreur de quota, réessayez.");
      return;
    }
    setGenerating(true);
    setGrids([]);
    setTimeout(() => {
      const out = [];
      for (let i = 0; i < count; i++) {
        out.push(generateOptimizedGrid(cfg, draws, strategy, { kenoPick }));
      }
      setGrids(out);
      setGenerating(false);
      const fb = out.filter((g) => g.fallback).length;
      if (fb) toast.info(`${fb} grille(s) en repli (critères très stricts)`);
      else toast.success(`${count} grille(s) générée(s)`);
    }, 350);
  };

  const copyGrid = (g) => {
    const txt = g.grid.join(", ") + (g.bonus.length ? ` | ${cfg.bonusName}: ${g.bonus.join(", ")}` : "");
    navigator.clipboard.writeText(txt);
    toast.success("Grille copiée");
  };

  const freeUsed = !usage?.is_premium && (usage?.counts?.[game] || 0) >= (usage?.free_limit || 1);

  return (
    <div>
      {/* Strategy selector */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {STRATEGIES.map((s) => {
          const active = strategy === s.id;
          return (
            <button key={s.id} onClick={() => setStrategy(s.id)} data-testid={`strategy-${s.id}`}
              className={`text-left rounded-2xl border-2 p-4 transition-all ${active ? "shadow-md" : "border-slate-200 bg-white hover:border-slate-300"}`}
              style={active ? { borderColor: cfg.primary, background: cfg.lightBg } : {}}>
              <div className="font-bold text-slate-900 flex items-center gap-2">
                <Zap className="w-4 h-4" style={{ color: cfg.primary }} /> {s.label}
              </div>
              <div className="text-xs text-slate-500 mt-1">{s.desc}</div>
            </button>
          );
        })}
      </div>

      {cfg.kenoMode && (
        <div className="flex items-center gap-3 mb-6 bg-white rounded-2xl border border-slate-200 p-4">
          <span className="font-bold text-slate-700 text-sm">Numéros à jouer :</span>
          <div className="flex gap-1.5 flex-wrap">
            {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((k) => (
              <button key={k} onClick={() => setKenoPick(k)} data-testid={`keno-pick-${k}`}
                className={`w-9 h-9 rounded-full font-bold text-sm transition-all ${kenoPick === k ? "text-white shadow-md scale-110" : "bg-slate-100 text-slate-600"}`}
                style={kenoPick === k ? { background: cfg.primary } : {}}>
                {k}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Generate buttons */}
      <div className="flex flex-wrap gap-3 mb-8">
        {[1, 5, 10].map((n) => {
          const locked = n > 1 && !usage?.is_premium;
          return (
            <Button key={n} onClick={() => generate(n)} disabled={generating}
              data-testid={n === 1 ? "generator-submit-btn" : `generator-submit-${n}`}
              className="rounded-full font-black px-6 py-6 text-white hover:scale-[1.03] transition-all shadow-lg"
              style={{ background: `linear-gradient(135deg, ${cfg.primary}, ${cfg.secondary})` }}>
              <Sparkles className="w-4 h-4 mr-1" /> Générer {n} grille{n > 1 ? "s" : ""}
              {locked && <Lock className="w-3.5 h-3.5 ml-1.5 opacity-80" />}
            </Button>
          );
        })}
      </div>

      {freeUsed && !usage?.is_premium && (
        <div className="mb-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 font-medium">
          Vous avez utilisé votre grille gratuite du jour pour {cfg.name}. Passez Premium pour des générations illimitées.
        </div>
      )}

      {/* Results */}
      <div className="grid md:grid-cols-2 gap-5">
        <AnimatePresence>
          {grids.map((g, idx) => {
            const sum = g.grid.reduce((a, b) => a + b, 0);
            const evens = g.grid.filter((x) => x % 2 === 0).length;
            const lvl = scoreLevel(g.score);
            return (
              <motion.div key={idx} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-2xl border border-slate-200 shadow-[0_4px_20px_-4px_rgba(0,59,128,0.08)] p-5"
                data-testid={`generated-grid-card-${idx}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full" style={{ background: cfg.lightBg, color: cfg.primary }}>
                    Grille #{idx + 1}
                  </span>
                  {g.fallback && <span className="text-[10px] font-bold text-amber-600 uppercase">Repli</span>}
                </div>
                <div className="flex items-center gap-5">
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {g.grid.map((n, i) => (
                        <LotteryBall key={n} number={n} variant={cfg.id} delay={i * 0.06} testId={`lottery-ball-${n}`} />
                      ))}
                    </div>
                    {g.bonus.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[10px] font-bold uppercase text-slate-400">{cfg.bonusName}</span>
                        {g.bonus.map((b, i) => (
                          <LotteryBall key={`b${b}`} number={b} variant={bonusVariant} size="sm" delay={0.3 + i * 0.06} testId={`lottery-bonus-ball-${b}`} />
                        ))}
                      </div>
                    )}
                  </div>
                  <ConformityGauge score={g.score} testId={`grid-score-${idx}`} />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="rounded-xl bg-slate-50 py-2">
                    <div className="text-[10px] font-bold uppercase text-slate-400">Somme</div>
                    <div className="font-black font-mono text-slate-800">{sum}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 py-2">
                    <div className="text-[10px] font-bold uppercase text-slate-400">Parité</div>
                    <div className="font-black font-mono text-slate-800">{evens}P/{g.grid.length - evens}I</div>
                  </div>
                  <div className="rounded-xl py-2" style={{ background: cfg.lightBg }}>
                    <div className="text-[10px] font-bold uppercase text-slate-400">Verdict</div>
                    <div className="font-black text-xs" style={{ color: lvl.color }}>{lvl.label.split(" ")[0]}</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Button onClick={() => copyGrid(g)} variant="outline" className="flex-1 rounded-full font-bold" data-testid={`copy-grid-${idx}`}>
                    <Copy className="w-4 h-4 mr-1" /> Copier
                  </Button>
                  {affiliateUrl && (
                    <PlayFdjButton url={affiliateUrl} game={game} gameColor="#D4001A" size="sm" testId={`grid-play-fdj-${idx}`} />
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {grids.length === 0 && !generating && (
        <div className="text-center py-16 text-slate-400">
          <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">Choisissez une stratégie puis générez vos grilles optimisées.</p>
        </div>
      )}
    </div>
  );
};
